import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, Buildings, ChartLineUp, Lock, Eye, Warning,
  ArrowCounterClockwise, Users, Handshake, CheckCircle,
  CaretRight, CaretLeft, CaretDown, Check, X,
  Lightning, ArrowRight, Upload, Robot, FileText,
  Gauge, Target, ListChecks, Sparkle, Info,
  PencilSimple, DownloadSimple, Trash, FloppyDisk, List,
  Brain, CircleNotch, Plus
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Domain icons mapping
const DOMAIN_ICONS = {
  governance: Buildings,
  risk_management: ChartLineUp,
  access_control: Lock,
  protection: Shield,
  detection: Eye,
  incident_response: Warning,
  recovery: ArrowCounterClockwise,
  awareness: Users,
  third_party: Handshake,
  assurance: CheckCircle
};

// Score definitions
const SCORE_DEFINITIONS = {
  0: { label: 'Not Implemented', shortLabel: 'None', color: 'bg-red-500', textColor: 'text-red-500' },
  1: { label: 'Ad Hoc', shortLabel: 'Ad Hoc', color: 'bg-red-400', textColor: 'text-red-400' },
  2: { label: 'Defined', shortLabel: 'Defined', color: 'bg-amber-500', textColor: 'text-amber-500' },
  3: { label: 'Managed', shortLabel: 'Managed', color: 'bg-lime-500', textColor: 'text-lime-500' },
  4: { label: 'Optimised', shortLabel: 'Optimal', color: 'bg-emerald-500', textColor: 'text-emerald-500' }
};

// Score Selector Component - Compact version
const ScoreSelector = ({ value, onChange, questionId, disabled }) => (
  <div className="flex gap-1.5" data-testid={`score-selector-${questionId}`}>
    {[0, 1, 2, 3, 4].map((score) => {
      const def = SCORE_DEFINITIONS[score];
      const isSelected = value === score;
      const isRiskFlag = score < 2;
      
      return (
        <button
          key={score}
          data-testid={`score-btn-${questionId}-${score}`}
          onClick={() => !disabled && onChange(questionId, score)}
          disabled={disabled}
          className={`relative flex flex-col items-center p-2 rounded-lg border transition-all min-w-[52px] ${
            isSelected 
              ? `${def.color} border-transparent text-white` 
              : `bg-background border-border hover:border-gold/50 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
          }`}
        >
          <span className={`text-lg font-mono font-bold ${isSelected ? 'text-white' : def.textColor}`}>
            {score}
          </span>
          <span className={`text-[9px] leading-tight ${isSelected ? 'text-white/80' : 'text-text-tertiary'}`}>
            {def.shortLabel}
          </span>
          {isRiskFlag && !isSelected && (
            <Warning size={10} className="absolute -top-1 -right-1 text-amber-500" weight="fill" />
          )}
        </button>
      );
    })}
  </div>
);

// Question Card - Simplified
const QuestionCard = ({ question, answer, onScoreChange, onNotesChange, evidenceStatus, onUploadEvidence, disabled }) => {
  const [showNotes, setShowNotes] = useState(false);
  const score = answer?.score;
  const needsEvidence = score !== undefined && score >= 2;
  const hasEvidence = evidenceStatus?.has_evidence;
  const evidenceValidated = evidenceStatus?.status === 'validated';
  const naviResult = evidenceStatus?.navi_result;
  const naviFeedback = evidenceStatus?.feedback;
  const validationPassed = naviResult === 'MEETS';
  
  // Status indicator
  const getStatusBadge = () => {
    if (score === undefined) return null;
    
    if (score < 2) {
      return (
        <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400 flex items-center gap-1">
          <Warning size={10} /> Risk Log
        </span>
      );
    }
    
    if (!hasEvidence) {
      return (
        <span className="px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400 flex items-center gap-1">
          <Upload size={10} /> Evidence Required
        </span>
      );
    }
    
    if (!evidenceValidated) {
      return (
        <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-400 flex items-center gap-1">
          <Robot size={10} /> Pending Validation
        </span>
      );
    }
    
    if (validationPassed) {
      return (
        <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400 flex items-center gap-1">
          <CheckCircle size={10} weight="fill" /> Validated
        </span>
      );
    }
    
    // Validation failed - show "Failed" badge
    return (
      <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400 flex items-center gap-1">
        <Warning size={10} weight="fill" /> Validated • Failed
      </span>
    );
  };
  
  return (
    <div className={`bg-background-surface border rounded-lg p-4 ${
      score !== undefined && score < 2 ? 'border-red-500/30' : 'border-border'
    }`}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
          score !== undefined && score < 2 ? 'bg-red-500/10' : 'bg-gold/10'
        }`}>
          <span className={`text-xs font-mono font-bold ${
            score !== undefined && score < 2 ? 'text-red-400' : 'text-gold'
          }`}>
            {question.id}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-text-primary">{question.question}</p>
          <p className="text-xs text-text-tertiary mt-1">{question.regulatory_reference}</p>
        </div>
        {getStatusBadge()}
      </div>
      
      {/* Score Selector */}
      <div className="mb-3">
        <ScoreSelector
          value={score}
          onChange={onScoreChange}
          questionId={question.id}
          disabled={disabled}
        />
      </div>
      
      {/* Evidence Upload (for score >= 2) */}
      {needsEvidence && (
        <div className="mb-3 p-3 bg-background rounded-lg border border-border/50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-text-tertiary">
              {hasEvidence ? (
                <span className="flex items-center gap-1">
                  <FileText size={12} className="text-purple-400" />
                  Evidence uploaded
                  {evidenceValidated && (
                    <span className={`ml-1 ${validationPassed ? 'text-emerald-400' : 'text-red-400'}`}>
                      • {validationPassed ? 'Validated' : 'Failed'}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-amber-400">Evidence required for scores 2-4</span>
              )}
            </div>
            <button
              onClick={() => onUploadEvidence(question.id)}
              className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-400 hover:bg-purple-500/20 transition-colors"
            >
              <Upload size={12} />
              {hasEvidence ? 'Add More' : 'Upload'}
            </button>
          </div>
          
          {/* NAVI Feedback Display - Under the evidence section */}
          {evidenceValidated && naviFeedback && (
            <div className={`mt-3 p-3 rounded-lg ${
              validationPassed 
                ? 'bg-emerald-500/5 border border-emerald-500/20' 
                : 'bg-red-500/5 border border-red-500/20'
            }`}>
              <div className="flex items-start gap-2">
                <Robot size={14} className={`flex-shrink-0 mt-0.5 ${
                  validationPassed ? 'text-emerald-400' : 'text-red-400'
                }`} />
                <div className="text-xs">
                  <p className={`font-medium ${validationPassed ? 'text-emerald-400' : 'text-red-400'}`}>
                    NAVI Feedback:
                  </p>
                  <p className="text-text-secondary mt-1 leading-relaxed">
                    {naviFeedback}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Notes Toggle */}
      <button
        onClick={() => setShowNotes(!showNotes)}
        className="flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary"
      >
        <CaretRight size={10} className={`transition-transform ${showNotes ? 'rotate-90' : ''}`} />
        {showNotes ? 'Hide' : 'Add'} Notes
      </button>
      
      {showNotes && (
        <textarea
          value={answer?.notes || ''}
          onChange={(e) => onNotesChange(question.id, e.target.value)}
          placeholder="Add notes or observations..."
          className="w-full mt-2 px-3 py-2 bg-background border border-border rounded text-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none resize-none"
          rows={2}
        />
      )}
    </div>
  );
};

// Domain Progress Card
const DomainProgressCard = ({ domain, questions, answers, isActive, isComplete, onClick }) => {
  const DomainIcon = DOMAIN_ICONS[domain.id] || Shield;
  const answeredCount = questions.filter(q => answers[q.id]?.score !== undefined).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;
  
  return (
    <button
      onClick={onClick}
      className={`w-full p-3 rounded-lg border text-left transition-all ${
        isActive 
          ? 'bg-gold/10 border-gold' 
          : isComplete 
          ? 'bg-emerald-500/5 border-emerald-500/30' 
          : 'bg-background-surface border-border hover:border-gold/30'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${
          isActive ? 'bg-gold/20' : isComplete ? 'bg-emerald-500/20' : 'bg-background'
        }`}>
          {isComplete ? (
            <CheckCircle size={16} className="text-emerald-400" weight="fill" />
          ) : (
            <DomainIcon size={16} className={isActive ? 'text-gold' : 'text-text-tertiary'} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium truncate ${isActive ? 'text-gold' : 'text-text-primary'}`}>
            {domain.name}
          </div>
          <div className="text-xs text-text-tertiary">
            {answeredCount}/{questions.length} answered
          </div>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-2 h-1 bg-background rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all ${isComplete ? 'bg-emerald-500' : 'bg-gold'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </button>
  );
};

// Evidence Upload Modal
const EvidenceUploadModal = ({ isOpen, onClose, questionId, assessmentId, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  if (!isOpen) return null;
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('question_id', questionId);
    
    try {
      const response = await axios.post(
        `${API}/cyber-diagnostics/assessments/${assessmentId}/upload-evidence`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        setUploadedFiles(prev => [...prev, {
          id: response.data.evidence_id,
          filename: file.name,
          status: 'pending',
          naviFeedback: null
        }]);
        toast.success('Evidence uploaded');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const handleAnalyze = async (evidenceId) => {
    setAnalyzing(true);
    
    const formData = new FormData();
    formData.append('question_id', questionId);
    formData.append('evidence_id', evidenceId);
    
    try {
      const response = await axios.post(
        `${API}/cyber-diagnostics/assessments/${assessmentId}/analyze-evidence`,
        formData
      );
      
      if (response.data.success) {
        const result = response.data.navi_result;
        const analysis = response.data.analysis || {};
        const assessment = analysis.assessment || {};
        
        // Extract feedback from NAVI analysis
        const feedback = assessment.detailed_feedback || 
                        assessment.summary || 
                        assessment.gaps_identified?.join('; ') ||
                        (result === 'MEETS' ? 'Evidence meets requirements' : 
                         result === 'PARTIALLY_MEETS' ? 'Evidence partially meets requirements - additional documentation may be needed' :
                         'Evidence does not meet requirements - please upload additional supporting documentation');
        
        setUploadedFiles(prev => prev.map(f => 
          f.id === evidenceId ? { 
            ...f, 
            status: 'analyzed', 
            naviResult: result,
            naviFeedback: feedback,
            validationPassed: result === 'MEETS'
          } : f
        ));
        
        // Pass result and feedback to parent
        onUploadComplete(questionId, { 
          status: 'validated', 
          navi_result: result, 
          has_evidence: true,
          feedback: feedback,
          validation_passed: result === 'MEETS'
        });
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background-surface border border-border rounded-lg w-full max-w-lg"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-text-primary">Upload Evidence</h3>
            <p className="text-xs text-text-tertiary mt-0.5">Question: {questionId}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-background rounded-lg">
            <X size={20} className="text-text-tertiary" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Upload Area */}
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-gold/50 transition-colors">
            <Upload size={32} className="mx-auto mb-3 text-text-tertiary" />
            <label className="cursor-pointer">
              <span className="text-sm text-gold hover:text-gold-hover">Click to upload</span>
              <span className="text-sm text-text-tertiary"> or drag and drop</span>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                disabled={uploading}
                accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.csv,.png,.jpg,.jpeg"
              />
            </label>
            <p className="text-xs text-text-tertiary mt-2">PDF, Word, Excel, Images (max 10MB)</p>
          </div>
          
          {/* Uploaded Files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="bg-background rounded-lg border border-border overflow-hidden">
                  {/* File Header */}
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-purple-400 flex-shrink-0" />
                      <span className="text-sm text-text-primary truncate">{file.filename}</span>
                    </div>
                    {file.status === 'pending' ? (
                      <button
                        onClick={() => handleAnalyze(file.id)}
                        disabled={analyzing}
                        className="flex items-center gap-1 px-2 py-1 bg-gold/10 text-gold text-xs rounded hover:bg-gold/20 disabled:opacity-50 flex-shrink-0"
                      >
                        {analyzing ? (
                          <>
                            <div className="w-3 h-3 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Robot size={12} />
                            Validate with NAVI
                          </>
                        )}
                      </button>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 flex-shrink-0 ${
                        file.naviResult === 'MEETS' 
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' 
                          : 'bg-red-500/10 text-red-400 border border-red-500/30'
                      }`}>
                        {file.naviResult === 'MEETS' ? (
                          <>
                            <CheckCircle size={12} weight="fill" />
                            Validated
                          </>
                        ) : (
                          <>
                            <Warning size={12} weight="fill" />
                            Failed
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  
                  {/* NAVI Feedback - Displayed under each document */}
                  {file.status === 'analyzed' && file.naviFeedback && (
                    <div className={`px-3 pb-3 pt-0`}>
                      <div className={`p-3 rounded-lg text-xs ${
                        file.naviResult === 'MEETS'
                          ? 'bg-emerald-500/5 border border-emerald-500/20'
                          : 'bg-red-500/5 border border-red-500/20'
                      }`}>
                        <div className="flex items-start gap-2">
                          <Robot size={14} className={`flex-shrink-0 mt-0.5 ${
                            file.naviResult === 'MEETS' ? 'text-emerald-400' : 'text-red-400'
                          }`} />
                          <div>
                            <p className={`font-medium mb-1 ${
                              file.naviResult === 'MEETS' ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                              NAVI Feedback:
                            </p>
                            <p className="text-text-secondary leading-relaxed">
                              {file.naviFeedback}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <div className="text-xs text-text-tertiary bg-background p-3 rounded-lg">
            <Info size={14} className="inline mr-1" />
            NAVI AI will analyze your evidence against the audit criteria. Evidence that meets requirements will be automatically validated.
          </div>
        </div>
        
        <div className="p-4 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gold text-gold-foreground rounded-sm text-sm hover:bg-gold-hover"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Maturity Ring Component
const MaturityRing = ({ score, size = 120 }) => {
  const maxScore = 4;
  const percentage = (score / maxScore) * 100;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (score >= 3) return '#34d399';
    if (score >= 2) return '#facc15';
    return '#ef4444';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#1a1a1a" strokeWidth={strokeWidth} />
        <motion.circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={getColor()} strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-mono font-bold text-text-primary">{score.toFixed(1)}</span>
        <span className="text-[10px] text-text-tertiary">of {maxScore}</span>
      </div>
    </div>
  );
};

// Main Component
const CyberDiagnosticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard'); // dashboard, wizard, results
  const [domains, setDomains] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Wizard state
  const [assessmentId, setAssessmentId] = useState(null);
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [evidenceValidations, setEvidenceValidations] = useState({});
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadQuestionId, setUploadQuestionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Current assessment results
  const [currentResults, setCurrentResults] = useState(null);
  
  // Evidence Review state
  const [evidenceReviewData, setEvidenceReviewData] = useState(null);
  const [loadingEvidenceReview, setLoadingEvidenceReview] = useState(false);
  const [processingDecision, setProcessingDecision] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(null);
  const [resultsTab, setResultsTab] = useState('summary'); // summary, evidence
  
  // Assessments list state
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [editName, setEditName] = useState('');
  const [editClientRef, setEditClientRef] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [downloadingAssessment, setDownloadingAssessment] = useState(null);
  const [deletingAssessment, setDeletingAssessment] = useState(null);
  
  // NAVI Analysis state
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [naviAnalysis, setNaviAnalysis] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  
  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [domainsRes, questionsRes, dashboardRes, assessmentsRes] = await Promise.all([
        axios.get(`${API}/cyber-diagnostics/domains`),
        axios.get(`${API}/cyber-diagnostics/questions`),
        axios.get(`${API}/cyber-diagnostics/dashboard`),
        axios.get(`${API}/cyber-diagnostics/assessments`)
      ]);
      
      setDomains(domainsRes.data.domains || []);
      setQuestions(questionsRes.data.questions || []);
      setDashboardData(dashboardRes.data);
      setAssessments(assessmentsRes.data.assessments || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load diagnostics data');
    } finally {
      setLoading(false);
    }
  };
  
  // Start new assessment
  const startAssessment = async () => {
    try {
      const response = await axios.post(`${API}/cyber-diagnostics/assessments/start`, {
        assessment_name: `Cyber Assessment - ${new Date().toLocaleDateString()}`
      });
      
      if (response.data.success) {
        setAssessmentId(response.data.assessment_id);
        setCurrentDomainIndex(0);
        setAnswers({});
        setEvidenceValidations({});
        setView('wizard');
      }
    } catch (error) {
      toast.error('Failed to start assessment');
    }
  };
  
  // Resume in-progress assessment
  const checkInProgressAssessment = async () => {
    try {
      const response = await axios.get(`${API}/cyber-diagnostics/assessments/in-progress`);
      if (response.data.has_in_progress) {
        const assessment = response.data.assessment;
        setAssessmentId(assessment.id);
        setAnswers(assessment.answers || {});
        setEvidenceValidations(assessment.evidence_validations || {});
        
        // Find current domain based on completed domains
        const completedDomains = assessment.domains_completed || [];
        const domainIndex = domains.findIndex(d => !completedDomains.includes(d.id));
        setCurrentDomainIndex(domainIndex >= 0 ? domainIndex : 0);
        
        setView('wizard');
        return true;
      }
    } catch (error) {
      console.error('Error checking in-progress assessment:', error);
    }
    return false;
  };
  
  // Save domain answers
  const saveDomainAnswers = async (domainId) => {
    if (!assessmentId) return;
    
    const domainQuestions = questions.filter(q => q.domain === domainId);
    const domainAnswers = domainQuestions
      .filter(q => answers[q.id]?.score !== undefined)
      .map(q => ({ question_id: q.id, ...answers[q.id] }));
    
    try {
      await axios.put(`${API}/cyber-diagnostics/assessments/${assessmentId}/save-domain`, {
        domain_id: domainId,
        answers: domainAnswers
      });
    } catch (error) {
      console.error('Error saving domain:', error);
    }
  };
  
  // Handle score change
  const handleScoreChange = (questionId, score) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], question_id: questionId, score }
    }));
    
    // Clear evidence validation if score changes to < 2
    if (score < 2) {
      setEvidenceValidations(prev => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };
  
  // Handle notes change
  const handleNotesChange = (questionId, notes) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], question_id: questionId, notes }
    }));
  };
  
  // Handle evidence upload
  const handleUploadEvidence = (questionId) => {
    setUploadQuestionId(questionId);
    setUploadModalOpen(true);
  };
  
  // Handle evidence upload complete
  const handleEvidenceComplete = (questionId, validation) => {
    setEvidenceValidations(prev => ({
      ...prev,
      [questionId]: validation
    }));
  };
  
  // Fetch evidence review data for completed assessment
  const fetchEvidenceReview = async (assId) => {
    setLoadingEvidenceReview(true);
    try {
      const response = await axios.get(`${API}/cyber-diagnostics/assessments/${assId}/evidence-review`);
      setEvidenceReviewData(response.data);
    } catch (error) {
      console.error('Error fetching evidence review:', error);
      toast.error('Failed to load evidence review data');
    } finally {
      setLoadingEvidenceReview(false);
    }
  };
  
  // Handle auditor evidence decision
  const handleEvidenceDecision = async (questionId, evidenceId, decision, reason = null) => {
    if (decision === 'reject' && !reason) {
      setShowRejectionModal({ questionId, evidenceId });
      return;
    }
    
    setProcessingDecision(`${questionId}-${evidenceId}`);
    try {
      const response = await axios.post(
        `${API}/cyber-diagnostics/assessments/${currentResults?.assessment_id || assessmentId}/evidence-decision`,
        {
          question_id: questionId,
          evidence_id: evidenceId,
          decision: decision,
          rejection_reason: reason
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Refresh evidence review data
        await fetchEvidenceReview(currentResults?.assessment_id || assessmentId);
        
        // Clear rejection modal if open
        setShowRejectionModal(null);
        setRejectionReason('');
      }
    } catch (error) {
      console.error('Error making evidence decision:', error);
      toast.error(error.response?.data?.detail || 'Failed to record decision');
    } finally {
      setProcessingDecision(null);
    }
  };
  
  // Submit rejection with reason
  const submitRejection = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    handleEvidenceDecision(showRejectionModal.questionId, showRejectionModal.evidenceId, 'reject', rejectionReason);
  };

  // Save & Analyze with NAVI
  const handleSaveAndAnalyze = async () => {
    const assessId = currentResults?.assessment_id || assessmentId;
    if (!assessId) {
      toast.error('No assessment selected');
      return;
    }
    
    const answeredCount = Object.keys(answers).length;
    if (answeredCount === 0 && !currentResults) {
      toast.error('Please answer at least one question before saving');
      return;
    }
    
    setSavingAnalysis(true);
    try {
      const response = await axios.post(
        `${API}/cyber-diagnostics/assessments/${assessId}/save-and-analyze`
      );
      
      if (response.data.success) {
        setNaviAnalysis(response.data.navi_analysis);
        setCurrentResults(prev => prev ? {
          ...prev,
          navi_analysis: response.data.navi_analysis,
          overall_score: response.data.overall_score,
          maturity_level: response.data.maturity_level
        } : null);
        setShowAnalysisModal(true);
        toast.success(`Assessment saved! ${response.data.risk_items_logged} risk items logged.`);
      }
    } catch (error) {
      console.error('Save and analyze error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save and analyze');
    } finally {
      setSavingAnalysis(false);
    }
  };

  // Navigate domains
  const goToNextDomain = async () => {
    const currentDomain = domains[currentDomainIndex];
    if (currentDomain) {
      await saveDomainAnswers(currentDomain.id);
    }
    
    if (currentDomainIndex < domains.length - 1) {
      setCurrentDomainIndex(prev => prev + 1);
    }
  };
  
  const goToPrevDomain = () => {
    if (currentDomainIndex > 0) {
      setCurrentDomainIndex(prev => prev - 1);
    }
  };
  
  // Edit assessment name
  const startEditing = (assessment) => {
    setEditingAssessment(assessment.id);
    setEditName(assessment.assessment_name || '');
    setEditClientRef(assessment.client_reference || '');
  };
  
  const cancelEditing = () => {
    setEditingAssessment(null);
    setEditName('');
    setEditClientRef('');
  };
  
  const saveAssessmentEdit = async () => {
    if (!editName.trim()) {
      toast.error('Assessment name is required');
      return;
    }
    
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/cyber-diagnostics/assessments/${editingAssessment}`, {
        assessment_name: editName.trim(),
        client_reference: editClientRef.trim()
      });
      
      toast.success('Assessment updated');
      cancelEditing();
      fetchData();
    } catch (error) {
      console.error('Failed to update assessment:', error);
      toast.error('Failed to update assessment');
    } finally {
      setSavingEdit(false);
    }
  };
  
  // Download assessment
  const downloadAssessment = async (assessmentId, format = 'pdf') => {
    setDownloadingAssessment(assessmentId);
    try {
      const response = await axios.get(
        `${API}/cyber-diagnostics/assessments/${assessmentId}/download?format=${format}`,
        { responseType: 'blob' }
      );
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `CyberAssessment.${format === 'word' ? 'docx' : 'pdf'}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Assessment downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download assessment');
    } finally {
      setDownloadingAssessment(null);
    }
  };
  
  // Delete assessment
  const deleteAssessment = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.')) {
      return;
    }
    
    setDeletingAssessment(assessmentId);
    try {
      await axios.delete(`${API}/cyber-diagnostics/assessments/${assessmentId}`);
      toast.success('Assessment deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete assessment:', error);
      toast.error('Failed to delete assessment');
    } finally {
      setDeletingAssessment(null);
    }
  };
  
  // Finalize assessment
  const finalizeAssessment = async () => {
    // Save current domain first
    const currentDomain = domains[currentDomainIndex];
    if (currentDomain) {
      await saveDomainAnswers(currentDomain.id);
    }
    
    // Check if all evidence requirements are met
    const answersWithEvidenceRequired = Object.entries(answers)
      .filter(([_, a]) => a.score >= 2)
      .map(([questionId, _]) => questionId);
    
    const missingEvidence = answersWithEvidenceRequired.filter(qId => {
      const validation = evidenceValidations[qId];
      return !validation?.has_evidence || validation?.status !== 'validated';
    });
    
    if (missingEvidence.length > 0) {
      toast.error(`Please upload and validate evidence for ${missingEvidence.length} items with score 2 or above`);
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/cyber-diagnostics/assessments/${assessmentId}/finalize`);
      
      if (response.data.success) {
        setCurrentResults({
          ...response.data.results,
          assessment_id: assessmentId
        });
        setView('results');
        setResultsTab('summary');
        toast.success('Assessment completed!');
        
        // Fetch evidence review data
        await fetchEvidenceReview(assessmentId);
        
        if (response.data.risk_log_count > 0) {
          toast.info(`${response.data.risk_log_count} items added to Risk Log`);
        }
        
        fetchData(); // Refresh dashboard
      } else {
        toast.error(response.data.message || 'Cannot finalize assessment');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize assessment');
    } finally {
      setSubmitting(false);
    }
  };
  
  // View assessment results
  const viewAssessment = async (id) => {
    try {
      const response = await axios.get(`${API}/cyber-diagnostics/assessments/${id}`);
      setCurrentResults({
        ...response.data.results,
        assessment_id: id
      });
      setView('results');
      setResultsTab('summary');
      
      // Fetch evidence review data
      await fetchEvidenceReview(id);
    } catch (error) {
      toast.error('Failed to load assessment');
    }
  };
  
  // Get current domain questions
  const currentDomain = domains[currentDomainIndex];
  const currentQuestions = currentDomain 
    ? questions.filter(q => q.domain === currentDomain.id)
    : [];
  
  // Calculate progress
  const totalAnswered = Object.values(answers).filter(a => a.score !== undefined).length;
  const totalQuestions = questions.length;
  const overallProgress = totalQuestions > 0 ? (totalAnswered / totalQuestions) * 100 : 0;
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            <span className="text-sm text-text-tertiary">Loading Diagnostics...</span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary">
                Cybercrime & Cyber Resilience Diagnostics
              </h1>
              <p className="text-sm text-text-secondary mt-1">
                Joint Standard 1 & 2 | Cybercrimes Act Compliance
              </p>
            </div>
            <div className="flex items-center gap-3">
              {view !== 'dashboard' && (
                <button
                  onClick={() => { setView('dashboard'); setCurrentResults(null); }}
                  className="px-4 py-2 text-sm text-text-secondary hover:text-gold"
                  data-testid="back-to-dashboard-btn"
                >
                  Back to Dashboard
                </button>
              )}
              {view === 'dashboard' && (
                <>
                  <Link
                    to="/risk-log"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-gold"
                    data-testid="risk-log-link"
                  >
                    <Warning size={18} />
                    Risk Log
                  </Link>
                  <button
                    onClick={startAssessment}
                    className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover"
                    data-testid="new-assessment-btn"
                  >
                    <Lightning size={18} />
                    New Assessment
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Dashboard View */}
        {view === 'dashboard' && (
          <div className="p-6 space-y-6">
            {/* Stats */}
            {dashboardData?.has_assessment ? (
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-background-surface border border-border rounded-lg p-4 flex items-center gap-4">
                  <MaturityRing score={dashboardData.overall_score} size={80} />
                  <div>
                    <div className="text-xs text-text-tertiary uppercase">Overall Maturity</div>
                    <div className="text-lg font-medium text-text-primary mt-1">
                      {SCORE_DEFINITIONS[Math.round(dashboardData.overall_score)]?.label || 'N/A'}
                    </div>
                  </div>
                </div>
                
                <div className="bg-background-surface border border-border rounded-lg p-4">
                  <div className="text-xs text-text-tertiary uppercase mb-2">Compliance</div>
                  <div className="text-3xl font-mono font-bold text-gold">{dashboardData.overall_percentage}%</div>
                </div>
                
                <div className="bg-background-surface border border-border rounded-lg p-4">
                  <div className="text-xs text-text-tertiary uppercase mb-2">Risk Items</div>
                  <div className="text-3xl font-mono font-bold text-red-400">
                    {dashboardData.flagged_items_count || 0}
                  </div>
                  <Link to="/risk-log" className="text-xs text-gold hover:text-gold-hover mt-1 inline-block">
                    View Risk Log →
                  </Link>
                </div>
                
                <div className="bg-background-surface border border-border rounded-lg p-4">
                  <div className="text-xs text-text-tertiary uppercase mb-2">Assessments</div>
                  <div className="text-3xl font-mono font-bold text-text-primary">
                    {assessments.length}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-background-surface border border-border rounded-lg p-8 text-center">
                <Shield size={48} className="mx-auto mb-4 text-gold/50" />
                <h3 className="text-lg font-medium text-text-primary mb-2">No Assessments Yet</h3>
                <p className="text-sm text-text-tertiary mb-4">
                  Start your first Cyber Resilience assessment to evaluate your compliance posture.
                </p>
                <button
                  onClick={startAssessment}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover"
                >
                  <Lightning size={18} />
                  Start Assessment
                </button>
              </div>
            )}
            
            {/* All Assessments - Comprehensive List */}
            {assessments.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-text-primary flex items-center gap-2">
                    <List size={16} className="text-gold" />
                    All Assessments ({assessments.length})
                  </h3>
                </div>
                <div className="space-y-2">
                  {assessments.map((assessment) => (
                    <div 
                      key={assessment.id}
                      className="bg-background-surface border border-border rounded-lg p-4"
                    >
                      {editingAssessment === assessment.id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-text-tertiary block mb-1">Assessment Name</label>
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                                placeholder="e.g., Q1 2026 Assessment"
                                data-testid={`edit-name-${assessment.id}`}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-text-tertiary block mb-1">Client Reference</label>
                              <input
                                type="text"
                                value={editClientRef}
                                onChange={(e) => setEditClientRef(e.target.value)}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
                                placeholder="e.g., ABC Corp, Client XYZ"
                                data-testid={`edit-client-ref-${assessment.id}`}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={cancelEditing}
                              className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveAssessmentEdit}
                              disabled={savingEdit}
                              className="px-3 py-1.5 bg-gold text-background rounded text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                              data-testid={`save-edit-${assessment.id}`}
                            >
                              {savingEdit ? (
                                <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                              ) : (
                                <FloppyDisk size={14} />
                              )}
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg font-mono font-bold text-gold">
                                {assessment.overall_score?.toFixed(1) || '-'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-text-primary truncate">
                                  {assessment.assessment_name || 'Unnamed Assessment'}
                                </span>
                                {assessment.client_reference && (
                                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs flex-shrink-0">
                                    {assessment.client_reference}
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  assessment.status === 'completed' 
                                    ? 'bg-emerald-500/20 text-emerald-400' 
                                    : 'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {assessment.status?.toUpperCase() || 'UNKNOWN'}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-text-tertiary mt-1">
                                <span>
                                  {new Date(assessment.completed_at || assessment.created_at).toLocaleDateString()}
                                </span>
                                <span>•</span>
                                <span>{assessment.compliance_percentage?.toFixed(0) || 0}% Compliance</span>
                                {assessment.risk_items_count > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-red-400">{assessment.risk_items_count} Risk Items</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 ml-4">
                            {/* View Button */}
                            <button
                              onClick={() => viewAssessment(assessment.id)}
                              className="px-3 py-1.5 bg-gold/10 text-gold rounded text-sm hover:bg-gold/20 transition-colors flex items-center gap-1"
                              title="View Assessment"
                              data-testid={`view-assessment-${assessment.id}`}
                            >
                              <Eye size={14} />
                              View
                            </button>
                            
                            {/* Edit Button */}
                            <button
                              onClick={() => startEditing(assessment)}
                              className="p-2 text-text-tertiary hover:text-gold hover:bg-gold/10 rounded transition-colors"
                              title="Edit Name"
                              data-testid={`edit-assessment-${assessment.id}`}
                            >
                              <PencilSimple size={16} />
                            </button>
                            
                            {/* Download Dropdown */}
                            <div className="relative group">
                              <button
                                disabled={downloadingAssessment === assessment.id}
                                className="p-2 text-text-tertiary hover:text-emerald-400 hover:bg-emerald-500/10 rounded transition-colors"
                                title="Download"
                              >
                                {downloadingAssessment === assessment.id ? (
                                  <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                                ) : (
                                  <DownloadSimple size={16} />
                                )}
                              </button>
                              <div className="absolute right-0 mt-1 w-32 bg-background-surface border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                                <button
                                  onClick={() => downloadAssessment(assessment.id, 'word')}
                                  className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-gold/10 hover:text-gold rounded-t-lg flex items-center gap-2"
                                >
                                  <FileText size={14} />
                                  Word
                                </button>
                                <button
                                  onClick={() => downloadAssessment(assessment.id, 'pdf')}
                                  className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-gold/10 hover:text-gold rounded-b-lg flex items-center gap-2"
                                >
                                  <FileText size={14} />
                                  PDF
                                </button>
                              </div>
                            </div>
                            
                            {/* Delete Button */}
                            <button
                              onClick={() => deleteAssessment(assessment.id)}
                              disabled={deletingAssessment === assessment.id}
                              className="p-2 text-text-tertiary hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              title="Delete"
                              data-testid={`delete-assessment-${assessment.id}`}
                            >
                              {deletingAssessment === assessment.id ? (
                                <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                              ) : (
                                <Trash size={16} />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Wizard View */}
        {view === 'wizard' && (
          <div className="flex">
            {/* Sidebar - Domain Navigation */}
            <div className="w-64 border-r border-border p-4 space-y-2 h-[calc(100vh-140px)] overflow-y-auto">
              <div className="text-xs text-text-tertiary uppercase mb-3">Domains</div>
              {domains.map((domain, index) => {
                const domainQuestions = questions.filter(q => q.domain === domain.id);
                const answeredCount = domainQuestions.filter(q => answers[q.id]?.score !== undefined).length;
                const isComplete = answeredCount === domainQuestions.length && answeredCount > 0;
                
                return (
                  <DomainProgressCard
                    key={domain.id}
                    domain={domain}
                    questions={domainQuestions}
                    answers={answers}
                    isActive={index === currentDomainIndex}
                    isComplete={isComplete}
                    onClick={() => setCurrentDomainIndex(index)}
                  />
                );
              })}
              
              {/* Overall Progress */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="text-xs text-text-tertiary mb-2">Overall Progress</div>
                <div className="h-2 bg-background rounded-full overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${overallProgress}%` }} />
                </div>
                <div className="text-xs text-text-tertiary mt-1">
                  {totalAnswered}/{totalQuestions} questions
                </div>
              </div>
            </div>
            
            {/* Main Content - Questions */}
            <div className="flex-1 p-6">
              {currentDomain && (
                <>
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      {React.createElement(DOMAIN_ICONS[currentDomain.id] || Shield, { 
                        size: 24, 
                        className: 'text-gold' 
                      })}
                      <h2 className="text-xl font-medium text-text-primary">{currentDomain.name}</h2>
                    </div>
                    <p className="text-sm text-text-tertiary">{currentDomain.description}</p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    {currentQuestions.map((question) => (
                      <QuestionCard
                        key={question.id}
                        question={question}
                        answer={answers[question.id]}
                        evidenceStatus={evidenceValidations[question.id]}
                        onScoreChange={handleScoreChange}
                        onNotesChange={handleNotesChange}
                        onUploadEvidence={handleUploadEvidence}
                      />
                    ))}
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <button
                      onClick={goToPrevDomain}
                      disabled={currentDomainIndex === 0}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CaretLeft size={16} />
                      Previous
                    </button>
                    
                    {currentDomainIndex === domains.length - 1 ? (
                      <button
                        onClick={finalizeAssessment}
                        disabled={submitting}
                        className="flex items-center gap-2 px-6 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover disabled:opacity-50"
                      >
                        {submitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-gold-foreground/30 border-t-gold-foreground rounded-full animate-spin" />
                            Finalizing...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={18} />
                            Complete Assessment
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={goToNextDomain}
                        className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover"
                      >
                        Next Domain
                        <CaretRight size={16} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Results View */}
        {view === 'results' && currentResults && (
          <div className="p-6 space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-background-surface border border-border rounded-lg p-4 flex items-center gap-4">
                <MaturityRing score={currentResults.overall_score} size={80} />
                <div>
                  <div className="text-xs text-text-tertiary uppercase">Maturity Score</div>
                  <div className="text-lg font-medium text-text-primary">
                    {SCORE_DEFINITIONS[Math.round(currentResults.overall_score)]?.label}
                  </div>
                </div>
              </div>
              
              <div className="bg-background-surface border border-border rounded-lg p-4">
                <div className="text-xs text-text-tertiary uppercase mb-2">Compliance</div>
                <div className="text-3xl font-mono font-bold text-gold">{currentResults.overall_percentage}%</div>
              </div>
              
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="text-xs text-red-400 uppercase mb-2">Auto-Flagged</div>
                <div className="text-3xl font-mono font-bold text-red-400">
                  {currentResults.summary?.auto_flagged_count || 0}
                </div>
                <div className="text-xs text-text-tertiary">Score &lt; 2</div>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="text-xs text-amber-400 uppercase mb-2">Evidence Failed</div>
                <div className="text-3xl font-mono font-bold text-amber-400">
                  {currentResults.summary?.evidence_failed_count || 0}
                </div>
                <div className="text-xs text-text-tertiary">NAVI validation</div>
              </div>
            </div>
            
            {/* Risk Log Link */}
            {(currentResults.summary?.auto_flagged_count > 0 || currentResults.summary?.evidence_failed_count > 0) && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Warning size={24} className="text-amber-400" />
                  <div>
                    <div className="text-sm font-medium text-text-primary">
                      {(currentResults.summary?.auto_flagged_count || 0) + (currentResults.summary?.evidence_failed_count || 0)} items added to Risk Log
                    </div>
                    <div className="text-xs text-text-tertiary">
                      Review and take action on flagged items
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveAndAnalyze}
                    disabled={savingAnalysis}
                    data-testid="cyber-save-analyze-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {savingAnalysis ? (
                      <>
                        <CircleNotch size={16} className="animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain size={16} />
                        Save & Analyze
                      </>
                    )}
                  </button>
                  <Link
                    to="/risk-log"
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-sm hover:bg-amber-600"
                  >
                    View Risk Log
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            )}
            
            {/* Save & Analyze Button (when no risk items) */}
            {!(currentResults.summary?.auto_flagged_count > 0 || currentResults.summary?.evidence_failed_count > 0) && (
              <div className="flex justify-end">
                <button
                  onClick={handleSaveAndAnalyze}
                  disabled={savingAnalysis}
                  data-testid="cyber-save-analyze-btn-alt"
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  {savingAnalysis ? (
                    <>
                      <CircleNotch size={16} className="animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Brain size={16} />
                      Save & Analyze with NAVI
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Results Tabs */}
            <div className="border-b border-border">
              <div className="flex gap-4">
                <button
                  onClick={() => setResultsTab('summary')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    resultsTab === 'summary' 
                      ? 'border-gold text-gold' 
                      : 'border-transparent text-text-tertiary hover:text-text-secondary'
                  }`}
                >
                  Summary & Domain Scores
                </button>
                <button
                  onClick={() => setResultsTab('evidence')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    resultsTab === 'evidence' 
                      ? 'border-gold text-gold' 
                      : 'border-transparent text-text-tertiary hover:text-text-secondary'
                  }`}
                >
                  Evidence Review
                  {evidenceReviewData?.pending_count > 0 && (
                    <span className="px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                      {evidenceReviewData.pending_count}
                    </span>
                  )}
                </button>
              </div>
            </div>
            
            {/* Summary Tab */}
            {resultsTab === 'summary' && (
              <div className="space-y-6">
                {/* Domain Breakdown */}
                <div>
                  <h3 className="text-sm font-medium text-text-primary mb-3">Domain Scores</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.values(currentResults.domain_scores || {}).map((domain) => (
                      <div key={domain.domain_id} className="bg-background-surface border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-text-primary">{domain.domain_name}</span>
                          <span className={`text-sm font-mono font-bold ${
                            domain.average_score >= 3 ? 'text-emerald-400' :
                            domain.average_score >= 2 ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {domain.average_score.toFixed(1)}
                          </span>
                        </div>
                        <div className="h-2 bg-background rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              domain.average_score >= 3 ? 'bg-emerald-500' :
                              domain.average_score >= 2 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${domain.percentage}%` }}
                          />
                        </div>
                        <div className="mt-2 text-xs text-text-tertiary">
                          {domain.questions_count} questions • {domain.percentage}% compliant
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Evidence Review Tab */}
            {resultsTab === 'evidence' && (
              <div className="space-y-6">
                {loadingEvidenceReview ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                  </div>
                ) : evidenceReviewData ? (
                  <>
                    {/* Evidence Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <div className="text-xs text-amber-400 uppercase mb-1">Pending Review</div>
                        <div className="text-2xl font-mono font-bold text-amber-400">
                          {evidenceReviewData.pending_count}
                        </div>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                        <div className="text-xs text-emerald-400 uppercase mb-1">Accepted</div>
                        <div className="text-2xl font-mono font-bold text-emerald-400">
                          {evidenceReviewData.accepted_count}
                        </div>
                      </div>
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <div className="text-xs text-red-400 uppercase mb-1">Rejected</div>
                        <div className="text-2xl font-mono font-bold text-red-400">
                          {evidenceReviewData.rejected_count}
                        </div>
                      </div>
                    </div>
                    
                    {/* Pending Evidence Items */}
                    {evidenceReviewData.pending_review.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
                          <Warning size={16} className="text-amber-400" />
                          Evidence Pending Auditor Review
                        </h3>
                        <div className="space-y-4">
                          {evidenceReviewData.pending_review.map((item) => (
                            <div 
                              key={`${item.question_id}-${item.evidence_id}`} 
                              className="bg-background-surface border border-border rounded-lg p-4"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-text-primary mb-1">
                                    {item.question_text}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-text-tertiary mb-3">
                                    <span className="bg-background px-2 py-0.5 rounded">{item.domain}</span>
                                    <span>Score: {item.score}</span>
                                    <span className="flex items-center gap-1">
                                      <FileText size={12} />
                                      {item.filename}
                                    </span>
                                  </div>
                                  
                                  {/* NAVI Recommendation */}
                                  <div className="bg-background rounded-lg p-3 mb-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Robot size={16} className="text-gold" />
                                      <span className="text-xs font-medium text-gold">NAVI Recommendation</span>
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        item.navi_analysis?.assessment_result === 'MEETS' 
                                          ? 'bg-emerald-500/20 text-emerald-400' 
                                          : item.navi_analysis?.assessment_result === 'PARTIALLY_MEETS'
                                          ? 'bg-amber-500/20 text-amber-400'
                                          : 'bg-red-500/20 text-red-400'
                                      }`}>
                                        {item.navi_analysis?.assessment_result || 'PENDING'}
                                      </span>
                                    </div>
                                    
                                    {item.navi_analysis?.justification && (
                                      <p className="text-xs text-text-secondary mb-2">
                                        {item.navi_analysis.justification}
                                      </p>
                                    )}
                                    
                                    {item.navi_analysis?.evidence_found?.length > 0 && (
                                      <div className="mb-2">
                                        <span className="text-xs text-emerald-400 font-medium">Evidence Found:</span>
                                        <ul className="text-xs text-text-tertiary ml-4 mt-1">
                                          {item.navi_analysis.evidence_found.slice(0, 3).map((e, i) => (
                                            <li key={i}>• {e}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {item.navi_analysis?.gaps_identified?.length > 0 && (
                                      <div className="mb-2">
                                        <span className="text-xs text-red-400 font-medium">Gaps Identified:</span>
                                        <ul className="text-xs text-text-tertiary ml-4 mt-1">
                                          {item.navi_analysis.gaps_identified.slice(0, 3).map((g, i) => (
                                            <li key={i}>• {g}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {item.navi_analysis?.recommendations?.length > 0 && (
                                      <div>
                                        <span className="text-xs text-blue-400 font-medium">Recommendations:</span>
                                        <ul className="text-xs text-text-tertiary ml-4 mt-1">
                                          {item.navi_analysis.recommendations.slice(0, 3).map((r, i) => (
                                            <li key={i}>• {r}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Decision Buttons */}
                                <div className="flex flex-col gap-2">
                                  <button
                                    onClick={() => handleEvidenceDecision(item.question_id, item.evidence_id, 'accept')}
                                    disabled={processingDecision === `${item.question_id}-${item.evidence_id}`}
                                    className="px-4 py-2 bg-emerald-500 text-white rounded-sm hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-2 text-sm"
                                    data-testid={`accept-evidence-${item.question_id}`}
                                  >
                                    {processingDecision === `${item.question_id}-${item.evidence_id}` ? (
                                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                      <Check size={16} />
                                    )}
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleEvidenceDecision(item.question_id, item.evidence_id, 'reject')}
                                    disabled={processingDecision === `${item.question_id}-${item.evidence_id}`}
                                    className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 disabled:opacity-50 flex items-center gap-2 text-sm"
                                    data-testid={`reject-evidence-${item.question_id}`}
                                  >
                                    <X size={16} />
                                    Reject
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Accepted Items */}
                    {evidenceReviewData.accepted.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
                          <CheckCircle size={16} className="text-emerald-400" />
                          Accepted Evidence ({evidenceReviewData.accepted_count})
                        </h3>
                        <div className="space-y-2">
                          {evidenceReviewData.accepted.map((item) => (
                            <div 
                              key={`${item.question_id}-${item.evidence_id}`}
                              className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center justify-between"
                            >
                              <div>
                                <div className="text-sm text-text-primary">{item.question_text}</div>
                                <div className="text-xs text-text-tertiary">{item.filename}</div>
                              </div>
                              <CheckCircle size={20} className="text-emerald-400" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Rejected Items */}
                    {evidenceReviewData.rejected.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
                          <X size={16} className="text-red-400" />
                          Rejected Evidence → Added to Risk Log ({evidenceReviewData.rejected_count})
                        </h3>
                        <div className="space-y-2">
                          {evidenceReviewData.rejected.map((item) => (
                            <div 
                              key={`${item.question_id}-${item.evidence_id}`}
                              className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm text-text-primary">{item.question_text}</div>
                                  <div className="text-xs text-text-tertiary">{item.filename}</div>
                                </div>
                                <X size={20} className="text-red-400" />
                              </div>
                              {item.rejection_reason && (
                                <div className="mt-2 text-xs text-red-400 bg-red-500/10 rounded p-2">
                                  <span className="font-medium">Reason:</span> {item.rejection_reason}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* No Evidence Items */}
                    {evidenceReviewData.total_evidence_items === 0 && (
                      <div className="text-center py-12 text-text-tertiary">
                        <FileText size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No evidence uploaded for this assessment</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-text-tertiary">
                    <p>No evidence data available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Rejection Reason Modal */}
        {showRejectionModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-background-surface border border-border rounded-lg w-full max-w-md"
            >
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-medium text-text-primary">Reject Evidence</h3>
                <p className="text-xs text-text-tertiary mt-1">Please provide a reason for rejection</p>
              </div>
              <div className="p-4">
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter rejection reason... (e.g., Document outdated, Missing required information, Insufficient evidence)"
                  className="w-full h-32 px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none focus:border-gold"
                  data-testid="rejection-reason-input"
                />
              </div>
              <div className="p-4 border-t border-border flex justify-end gap-3">
                <button
                  onClick={() => { setShowRejectionModal(null); setRejectionReason(''); }}
                  className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRejection}
                  disabled={!rejectionReason.trim() || processingDecision}
                  className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 disabled:opacity-50 flex items-center gap-2 text-sm"
                  data-testid="submit-rejection-btn"
                >
                  {processingDecision ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <X size={16} />
                  )}
                  Reject & Add to Risk Log
                </button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Evidence Upload Modal */}
        <EvidenceUploadModal
          isOpen={uploadModalOpen}
          onClose={() => { setUploadModalOpen(false); setUploadQuestionId(null); }}
          questionId={uploadQuestionId}
          assessmentId={assessmentId}
          onUploadComplete={handleEvidenceComplete}
        />
        
        {/* NAVI Analysis Modal */}
        {showAnalysisModal && naviAnalysis && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-surface border border-gold/30 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            >
              <div className="p-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Brain size={24} className="text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-text-primary">NAVI Analysis Complete</h3>
                    <p className="text-xs text-text-tertiary">Cyber Resilience Assessment</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAnalysisModal(false)}
                  className="p-2 hover:bg-background rounded"
                >
                  <X size={20} className="text-text-tertiary" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                {/* Score Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-background rounded-lg p-4 text-center">
                    <p className="text-xs text-text-tertiary mb-1">Overall Score</p>
                    <p className="text-3xl font-mono font-bold text-gold">
                      {currentResults?.overall_percentage || 0}%
                    </p>
                  </div>
                  <div className="bg-background rounded-lg p-4 text-center">
                    <p className="text-xs text-text-tertiary mb-1">Maturity Level</p>
                    <p className="text-lg font-semibold text-emerald-400">
                      {currentResults?.maturity_level || SCORE_DEFINITIONS[Math.round(currentResults?.overall_score || 0)]?.label}
                    </p>
                  </div>
                  <div className="bg-background rounded-lg p-4 text-center">
                    <p className="text-xs text-text-tertiary mb-1">Risk Items</p>
                    <p className="text-3xl font-mono font-bold text-red-400">
                      {(currentResults?.summary?.auto_flagged_count || 0) + (currentResults?.summary?.evidence_failed_count || 0)}
                    </p>
                  </div>
                </div>
                
                {/* NAVI Analysis Content */}
                <div className="prose prose-invert prose-sm max-w-none">
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <div className="whitespace-pre-wrap text-text-secondary text-sm leading-relaxed">
                      {naviAnalysis}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t border-border flex justify-between items-center">
                <Link
                  to="/risk-log"
                  className="text-sm text-gold hover:underline flex items-center gap-1"
                >
                  <Warning size={16} />
                  View Risk Log
                </Link>
                <div className="flex gap-2">
                  <Link
                    to="/themes/cybersecurity"
                    className="px-4 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 text-sm flex items-center gap-2"
                  >
                    <ChartLineUp size={16} />
                    View Dashboard
                  </Link>
                  <button
                    onClick={() => setShowAnalysisModal(false)}
                    className="px-4 py-2 bg-gold text-gold-foreground rounded font-medium hover:bg-gold-hover text-sm"
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CyberDiagnosticsPage;
